import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';

import checkAndUpdateExcludeFromSelfServiceAction from '../actions/checkAndUpdateExcludeFromSelfServiceAction';
import { setCreateShareLinkStatus, setShareModalShareLink } from '../actions/shareModalActions';
import setMeetingTypeExtensionDetails from '../orchestrators/setMeetingTypeExtensionDetails';
import getEntryPoint from '../selectors/getEntryPoint';
import getMailboxPuid from '../selectors/getMailboxPuid';
import generateBookingUrl from '../services/generateBookingUrl';
import type ServicesRequestStatus from '../store/schema/ServicesRequestStatus';
import { SHARE_BOOKING_PAGE_ID } from '../store/schema/SharedViewState';
import store from '../store/Store';
import { getUserMailboxGuid } from './getMailboxGuid';
import updateMeetingTypePrivacy from './updateMeetingTypePrivacy';
import { isFeatureEnabled } from 'owa-feature-flags';
import createBookingUrl from 'bookings-shared/lib/c1/services/createBookingUrl';
import { getUserConfiguration } from 'owa-session-store';
import type FeatureSource from 'bookings-shared/lib/c1/constants/source';

import type GenerateBookingUrlResponse from '../services/schema/GenerateBookingUrlResponse';
import { getGuid } from 'owa-guid';
import { addB2CustomData } from 'bookings-shared/lib/c1/utils/bookingTelemetry';
import fixBookingUrlDomain from './fixBookingUrlDomain';
import { isMsalEnabledForBusiness } from 'owa-msaljs/lib/isMsalEnabledForBusiness';
/**
 * Function to generate share link for booking page and meeting types
 */
export default async function getBookingUrl(): Promise<string> {
    setCreateShareLinkStatus(1);

    const perfDatapoint = new PerformanceDatapoint('generateBookingUrl');
    addB2CustomData(perfDatapoint);
    perfDatapoint.addCustomData({
        meetingTypeId: store.sharedViewState.shareEventId ?? '',
        isSingleUse: store.sharedViewState.isSingleUse.toString(),
        isAttendeeAuthRequired: store.sharedViewState.isAttendeeAuthRequired ?? '',
        isPublic: !store.sharedViewState.isPrivate,
        entryPoint: getEntryPoint(),
        guid: getGuid(),
        mailboxGuid: getUserMailboxGuid(),
        bookingMailboxPuid: getMailboxPuid(),
    });

    // If share booking page, return public booking page link
    if (store.sharedViewState.shareEventId == SHARE_BOOKING_PAGE_ID) {
        setCreateShareLinkStatus(2);
        return Promise.resolve(store.sharedViewState.shareLink);
    }

    // If share meeting type, get meeting type link
    const meetingType = store.meetingTypes?.get(store.sharedViewState.shareEventId);

    var bookingUrl = '';
    if (isFeatureEnabled('bookwithme-c1B2Migration')) {
        const businessSmtp = getUserConfiguration().SessionSettings?.UserPrincipalName || '';
        const response = await createBookingUrl(
            businessSmtp,
            store.sharedViewState.shareEventId,
            store.sharedViewState.isSingleUse,
            'BookWithMe'
        );
        bookingUrl = response?.bookingUrl || '';
        // Temporary change done to support MSAL migration changes
        if (isMsalEnabledForBusiness()) {
            bookingUrl += '&ismsaljsauthenabled';
        }
        store.linkGeneratedServiceIds.push(store.sharedViewState.shareEventId);
    } else {
        // Get and set meeting type extension information
        await setMeetingTypeExtensionDetails(store.sharedViewState.shareEventId);
        // If meeting type is inactive === private + not shared, update privacy setting before sharing
        // Check excludeFromSelfService value for both meeting type and meeting type extension, if one of them is true, then update
        if (
            meetingType?.ExcludeFromSelfService ||
            !store.meetingTypeExtension ||
            store.meetingTypeExtension?.excludeFromSelfService
        ) {
            await updateMeetingTypePrivacy(
                store.sharedViewState.shareEventId,
                false /*makePublic*/,
                false /*excludeFromService*/
            );
        }
        // Generate booking url
        let result: GenerateBookingUrlResponse | null = null;
        const mailboxGuid = getUserMailboxGuid();
        if (store.sharedViewState.isPrivate) {
            result = await generateBookingUrl({
                itemId: store.sharedViewState.shareEventId,
                isSingleUse: store.sharedViewState.isSingleUse.toString() /*isSingleUse*/,
                isAttendeeAuthRequired:
                    store.sharedViewState.isAttendeeAuthRequired.toString() /*isAttendeeAuthRequired*/,
                mailboxGuid /* mailboxGuid as returned by get-mailbox in parameter ExchangeGuid, required when URL sent to attendee has address obscured */,
            });
        } else {
            result = await generateBookingUrl({
                itemId: store.sharedViewState.shareEventId,
                isPublic: true /*isPublic*/,
                mailboxGuid,
            });
        }
        bookingUrl = result?.bookingUrl || '';
    }
    if (bookingUrl) {
        /**
         * Currently, the booking URL returned from the API has a hard-coded domain outlook.office.com.
         * We need to replace it with the current domain for it to work correctly for GCCH/DOD.
         */
        const updatedBookingUrl = fixBookingUrlDomain(bookingUrl);
        setShareModalShareLink(updatedBookingUrl);
        // Make call to verify MTE.excludeFromSelfService = false
        // TODO: remove this check after substrate fix override issue
        if (!isFeatureEnabled('bookwithme-c1B2Migration')) {
            checkAndUpdateExcludeFromSelfServiceAction(store.sharedViewState.shareEventId, false);
        }
        setCreateShareLinkStatus(2);
        perfDatapoint.end();
        return Promise.resolve(updatedBookingUrl);
    } else {
        setCreateShareLinkStatus(4);
        perfDatapoint.endWithError(DatapointStatus.ServerError);
        return Promise.reject(new Error('No returned booking URL'));
    }
}
